// Layout.js — Mobile responsive (hamburger + drawer) + Desktop inchangé
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import axios from 'axios';
import logo from '../assets/logo_original.png';
import theme from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Hook: true si largeur < 768px
const useIsMobile = () => {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
};

function Layout() {
  const { logout, user, token } = useAuth();
  const location = useLocation();
  const isAdmin  = user?.role === 'admin';
  const isMobile = useIsMobile();
  const [nbNotif,  setNbNotif]  = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  // Fermer drawer à chaque navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Bloquer scroll body quand drawer ouvert
  useEffect(() => {
    document.body.style.overflow = (isMobile && menuOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, menuOpen]);

  // Badge notifications
  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/notifications/count`,
        { headers: { Authorization: `Bearer ${token}` } });
      setNbNotif(r.data.count || 0);
    } catch { /* silencieux */ }
  }, [token]);

  useEffect(() => {
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => clearInterval(iv);
  }, [fetchCount]);

  useEffect(() => {
    if (location.pathname !== '/notifications') fetchCount();
  }, [location.pathname, fetchCount]);

  const menuItems = [
    { path: '/dashboard',      label: '📊 Dashboard',              show: 'all'   },
    { path: '/demandes',       label: '📬 Traiter les demandes',   show: 'admin' },
    { path: '/candidats',      label: '👤 Candidatures',           show: 'admin' },
    { path: '/clients',        label: '👥 Mes Clients',            show: 'admin' },
    { path: '/collaborateurs', label: '👔 Collaborateurs',         show: 'admin' },
    { path: '/prestations',    label: '🔧 Prestations',            show: 'admin' },
    { path: '/devis',          label: '📄 Devis',                  show: 'all'   },
    { path: '/factures',       label: '📑 Factures',               show: 'all'   },
    { path: '/facturespro',    label: '🌐 Factures Achat (Peppol)',show: 'admin' },
    { path: '/notes-frais',    label: '🧾 Notes de frais',         show: 'admin' },
    { path: '/notes-credit',   label: '💳 Notes de crédit',        show: 'all'   },
    { path: '/paiements',      label: '💰 Paiements',              show: 'all'   },
    { path: '/produits',       label: '☀️ Catalogue',               show: 'all'   },
    { path: '/courriers',      label: '📝 Courriers & Docs',       show: 'admin' },
    { path: '/deplacements',   label: '🚗 Déplacements',           show: 'admin' },
    { path: '/tva',           label: '🧮 Déclaration TVA',        show: 'admin' },
    { path: '/notifications',  label: '🔔 Notifications',          show: 'all', badge: nbNotif },
  ];

  const filtered = menuItems.filter(it =>
    it.show === 'all' || (it.show === 'admin' && isAdmin));
  const isActive = p => location.pathname === p;

  const SidebarContent = () => {
    // Recalculer isAdmin + filtered à l'intérieur pour éviter
    // tout problème de closure stale sur React re-renders
    const _isAdmin   = user?.role === 'admin';
    const _filtered  = menuItems.filter(it =>
      it.show === 'all' || (it.show === 'admin' && _isAdmin));
    return (
    <>
      <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <img src={logo} alt="MKW Solutions"
          style={{ width: '100%', maxWidth: '160px', height: 'auto', borderRadius: '8px', display: 'block' }} />
      </div>
      <ul style={{ flex: 1, padding: '12px 0', margin: 0 }}>
        {_filtered.map(it => (
          <li key={it.path} style={{ listStyle: 'none' }}>
            <Link to={it.path} style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '11px 16px', textDecoration: 'none', fontSize: '14px',
              color: 'rgba(255,255,255,0.82)', transition: 'background 0.15s',
              background: isActive(it.path) ? 'rgba(79,109,137,0.35)' : 'transparent',
              borderLeft: isActive(it.path) ? '3px solid #4f6d89' : '3px solid transparent',
              fontWeight: isActive(it.path) ? '600' : '400',
            }}>
              <span style={{ flex: 1 }}>{it.label}</span>
              {it.badge > 0 && (
                <span style={{
                  background: '#e74c3c', color: 'white', fontSize: '11px',
                  fontWeight: '700', padding: '2px 7px', borderRadius: '10px',
                  minWidth: '18px', textAlign: 'center', lineHeight: '16px',
                }}>
                  {it.badge > 99 ? '99+' : it.badge}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
      <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', overflow: 'hidden' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
            background: theme.primary || '#4f6d89',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: '700', fontSize: '14px',
          }}>
            {(user?.nom || 'U')[0].toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.prenom} {user?.nom}
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>
              {_isAdmin ? '👑 Admin' : '👤 Client'}
            </p>
          </div>
        </div>
        {nbNotif > 0 && (
          <Link to="/notifications" style={{
            display: 'block', textAlign: 'center', padding: '8px',
            background: 'rgba(231,76,60,0.15)', borderRadius: '8px',
            color: '#e74c3c', fontSize: '12px', fontWeight: '600',
            textDecoration: 'none', marginBottom: '8px',
          }}>
            🔔 {nbNotif} notification{nbNotif > 1 ? 's' : ''} non lue{nbNotif > 1 ? 's' : ''}
          </Link>
        )}
        <button onClick={logout} style={{
          width: '100%', padding: '9px',
          background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
        }}>
          🚪 Déconnexion
        </button>
      </div>
    </>
    );
  };

  const BG = 'linear-gradient(180deg, #2c3e50 0%, #34495e 100%)';

  // ── MOBILE (<768px) ──────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>

        {/* Top bar fixe : 3 colonnes strictes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '56px 1fr 56px',
          alignItems: 'center',
          height: '56px',
          minHeight: '56px',
          background: BG,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          flexShrink: 0,
          position: 'relative',
          zIndex: 10,
        }}>
          {/* Gauche : hamburger OU bouton retour */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px' }}>
            {location.pathname !== '/dashboard' ? (
              <button
                onClick={() => window.history.back()}
                aria-label="Retour"
                title="Retour"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', fontSize: '20px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ‹
              </button>
            ) : (
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Menu"
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', padding: '0' }}
              >
                <span style={{ display: 'block', width: '22px', height: '2px', background: 'rgba(255,255,255,0.9)', borderRadius: '2px', transition: 'transform 0.25s', ...(menuOpen && { transform: 'translateY(7px) rotate(45deg)' }) }} />
                <span style={{ display: 'block', width: '22px', height: '2px', background: 'rgba(255,255,255,0.9)', borderRadius: '2px', transition: 'opacity 0.25s', ...(menuOpen && { opacity: 0 }) }} />
                <span style={{ display: 'block', width: '22px', height: '2px', background: 'rgba(255,255,255,0.9)', borderRadius: '2px', transition: 'transform 0.25s', ...(menuOpen && { transform: 'translateY(-7px) rotate(-45deg)' }) }} />
              </button>
            )}
          </div>

          {/* Centre : logo */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img src={logo} alt="MKW" style={{ height: '32px', width: 'auto', borderRadius: '4px' }} />
          </div>

          {/* Droite : notifications */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Link to="/notifications" style={{ position: 'relative', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <span style={{ fontSize: '22px' }}>🔔</span>
              {nbNotif > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-6px',
                  background: '#e74c3c', color: 'white',
                  fontSize: '10px', fontWeight: '700',
                  padding: '1px 4px', borderRadius: '8px', minWidth: '16px', textAlign: 'center',
                }}>
                  {nbNotif > 99 ? '99+' : nbNotif}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Overlay */}
        {menuOpen && (
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }}
          />
        )}

        {/* Drawer */}
        <div style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, width: '260px',
          background: BG, display: 'flex', flexDirection: 'column',
          zIndex: 500, overflowY: 'auto',
          transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: '4px 0 20px rgba(0,0,0,0.4)',
        }}>
          <SidebarContent />
        </div>

        {/* Contenu scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: theme.pageBg || '#f4f7fa', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          <Outlet />
        </div>
      </div>
    );
  }

  // ── DESKTOP/TABLETTE (>=768px) — INCHANGÉ ───────────────────────────────
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <nav style={{
        width: '220px', minWidth: '220px',
        background: BG, display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
      }}>
        <SidebarContent />
      </nav>
      <main style={{ flex: 1, background: theme.pageBg || '#f4f7fa', overflowY: 'auto' }}>
        <Outlet />
      </main>
    </div>
  );
}

export default Layout;
